import type { OrganizationSecretSummary } from "@api-client";
import {
  DataTableBlock,
  type DataTableColumn,
} from "@sylviq/ui-react-web/blocks/table";
import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { type FormEvent, useEffect, useRef, useState } from "react";
import {
  ApiError,
  createOrganizationSecret,
  deleteOrganizationSecret,
  listOrganizationSecrets,
  listOrganizations,
  replaceOrganizationSecret,
  replaceOrganizationSecretBindings,
  replaceOrganizationSecretGrants,
} from "@/lib/api";
import { useSession } from "@/lib/session";
import {
  OrganizationNavigation,
  organizationViewHref,
  type ConsoleOrganizationOption,
} from "@/features/organizations/OrganizationNavigation";

type PageState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | {
      status: "loaded";
      organizationId: string;
      organizationName: string;
      organizations: ConsoleOrganizationOption[];
      secrets: OrganizationSecretSummary[];
    };

type CreateForm = {
  name: string;
  value: string;
  comment: string;
};

type EditMode = "value" | "comment";

type AccessGrant = OrganizationSecretSummary["grants"][number];
type AccessBinding = OrganizationSecretSummary["bindings"][number];

type EditForm = {
  value: string;
  comment: string;
};

const EMPTY_CREATE: CreateForm = { name: "", value: "", comment: "" };
const EMPTY_EDIT: EditForm = { value: "", comment: "" };

const MAX_SECRET_NAME_CHARS = 80;
const MAX_SECRET_VALUE_BYTES = 8192;
const MAX_SECRET_COMMENT_CHARS = 200;
const MAX_SECRET_PURPOSE_CHARS = 200;

const TIMESTAMP_FORMATTER = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

function utf8Bytes(value: string): number {
  return new TextEncoder().encode(value).length;
}

function validateName(name: string): string | null {
  if (!name) return "Enter a name for the secret.";
  if (name.length > MAX_SECRET_NAME_CHARS) {
    return "The name must be 80 characters or fewer.";
  }
  if (!/^[a-z0-9_-]+$/.test(name)) {
    return "Use lowercase letters, digits, underscores, and hyphens only.";
  }
  return null;
}

function validateValue(value: string): string | null {
  if (!value) return "Enter a value for the secret.";
  if (value.includes("\u0000")) return "The value cannot contain NUL characters.";
  if (utf8Bytes(value) > MAX_SECRET_VALUE_BYTES) {
    return "The value must be 8192 bytes or fewer.";
  }
  return null;
}

function validateOptionalValue(value: string): string | null {
  return value ? validateValue(value) : null;
}

function validateComment(comment: string): string | null {
  if (Array.from(comment).length > MAX_SECRET_COMMENT_CHARS) {
    return "The comment must be 200 characters or fewer.";
  }
  return null;
}

function validateExtensionPart(value: string, label: string): string | null {
  if (value.length < 2 || value.length > 40) {
    return `${label} must be 2 to 40 characters.`;
  }
  if (!/^[a-z0-9-]+$/.test(value)) {
    return `${label} may use lowercase letters, digits, and hyphens only.`;
  }
  return null;
}

function validateExtension(publisher: string, key: string): string | null {
  return (
    validateExtensionPart(publisher, "Publisher") ??
    validateExtensionPart(key, "Extension key")
  );
}

function validatePurpose(purpose: string): string | null {
  if (!purpose) return "Enter a binding purpose.";
  if (Array.from(purpose).length > MAX_SECRET_PURPOSE_CHARS) {
    return "The purpose must be 200 characters or fewer.";
  }
  if (!/^[a-z0-9_:-]+$/.test(purpose)) {
    return "Use lowercase letters, digits, underscores, hyphens, and colons only.";
  }
  return null;
}

function identityKey(identity: { publisher: string; key: string }): string {
  return `${identity.publisher}/${identity.key}`;
}

function groupedPurposes(
  bindings: AccessBinding[],
): Map<string, { publisher: string; key: string; purposes: string[] }> {
  const grouped = new Map<
    string,
    { publisher: string; key: string; purposes: string[] }
  >();
  for (const binding of bindings) {
    const identity = identityKey(binding);
    const entry = grouped.get(identity) ?? {
      publisher: binding.publisher,
      key: binding.key,
      purposes: [],
    };
    entry.purposes.push(binding.purpose);
    grouped.set(identity, entry);
  }
  return grouped;
}

function safeError(
  cause: unknown,
  fallback: string,
  conflictMessage?: string,
): string {
  if (cause instanceof ApiError) {
    if (cause.status === 403) {
      return "You do not have permission to manage secrets for this organization.";
    }
    if (cause.status === 404) return "That secret no longer exists.";
    if (cause.status === 409 && conflictMessage) return conflictMessage;
  }
  return fallback;
}

function formatTimestamp(value: string | null): string {
  if (!value) return "Never";
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime())
    ? TIMESTAMP_FORMATTER.format(parsed)
    : "Unavailable";
}

function grantLabel(grant: OrganizationSecretSummary["grants"][number]): string {
  return `${grant.publisher}/${grant.key}`;
}

const SECRET_COLUMNS: readonly DataTableColumn<OrganizationSecretSummary>[] = [
  {
    key: "name",
    header: "Name",
    cell: (secret) => <div className="font-medium">{secret.name}</div>,
  },
  {
    key: "comment",
    header: "Comment",
    cell: (secret) => (
      <div className="max-w-xs break-words">{secret.comment || "—"}</div>
    ),
  },
  {
    key: "replaced",
    header: "Last replaced",
    cell: (secret) =>
      secret.replaced_at ? (
        <time dateTime={secret.replaced_at}>
          {formatTimestamp(secret.replaced_at)}
        </time>
      ) : (
        <Badge tone="neutral">Never</Badge>
      ),
  },
  {
    key: "grants",
    header: "Granted extensions",
    cell: (secret) => (
      <div className="max-w-xs break-words">
        {secret.grants.length
          ? secret.grants.map(grantLabel).join(", ")
          : "None"}
      </div>
    ),
  },
  {
    key: "bindings",
    header: "Binding purposes",
    cell: (secret) => (
      <div className="max-w-xs break-words">
        {secret.bindings.length
          ? secret.bindings.map((binding) => binding.purpose).join(", ")
          : "None"}
      </div>
    ),
  },
];

export function OrganizationSecretsPage({
  organizationId,
}: {
  organizationId: string;
}) {
  const { state } = useSession();
  const userId = state.status === "authenticated" ? state.user.id : null;
  const mfaVerified =
    state.status === "authenticated" ? state.user.mfa_verified !== false : null;
  const [page, setPage] = useState<PageState>({ status: "loading" });
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateForm>(EMPTY_CREATE);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const createValueRef = useRef<HTMLInputElement>(null);
  const [editTarget, setEditTarget] = useState<OrganizationSecretSummary | null>(
    null,
  );
  const [editMode, setEditMode] = useState<EditMode | null>(null);
  const [editForm, setEditForm] = useState<EditForm>(EMPTY_EDIT);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const editValueRef = useRef<HTMLInputElement>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<OrganizationSecretSummary | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [accessTarget, setAccessTarget] =
    useState<OrganizationSecretSummary | null>(null);
  const [accessGrants, setAccessGrants] = useState<AccessGrant[]>([]);
  const [accessBindings, setAccessBindings] = useState<AccessBinding[]>([]);
  const [accessPublisher, setAccessPublisher] = useState("");
  const [accessKey, setAccessKey] = useState("");
  const [accessPurpose, setAccessPurpose] = useState("");
  const [accessBusy, setAccessBusy] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [selectedSecretIds, setSelectedSecretIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (state.status !== "authenticated") return;
    let cancelled = false;
    setPage({ status: "loading" });
    setNotice(null);
    void (async () => {
      try {
        const organizations = await listOrganizations();
        if (cancelled) return;
        const organization = organizations.find(
          (candidate) => candidate.id === organizationId,
        );
        if (!organization) {
          setPage({
            status: "error",
            message: "You do not have access to this organization.",
          });
          return;
        }
        if (organization.can_manage_secrets !== true) {
          setPage({
            status: "error",
            message:
              "You do not have permission to manage secrets for this organization.",
          });
          return;
        }
        if (mfaVerified === false) {
          setPage({
            status: "error",
            message:
              "Verify your second factor before managing organization secrets.",
          });
          return;
        }
        const secrets = await listOrganizationSecrets(organization.id);
        if (!cancelled) {
          setPage({
            status: "loaded",
            organizationId: organization.id,
            organizationName: organization.name,
            organizations: organizations.map((item) => ({
              id: item.id,
              name: item.name,
              canManageSecrets: item.can_manage_secrets,
            })),
            secrets,
          });
        }
      } catch {
        if (!cancelled) {
          setPage({
            status: "error",
            message:
              "Organization secrets could not be loaded. Please try again.",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId, state.status, userId, mfaVerified]);

  function openCreate() {
    setCreateOpen(true);
    setCreateForm(EMPTY_CREATE);
    setCreateError(null);
    setNotice(null);
  }

  function closeCreate() {
    if (createBusy) return;
    setCreateOpen(false);
    setCreateForm(EMPTY_CREATE);
    setCreateError(null);
    if (createValueRef.current) createValueRef.current.value = "";
  }

  function clearCreateValue() {
    setCreateForm((current) => ({ ...current, value: "" }));
    if (createValueRef.current) createValueRef.current.value = "";
  }

  async function submitCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (page.status !== "loaded" || createBusy) return;
    const name = createForm.name.trim();
    const value = createForm.value;
    const comment = createForm.comment.trim();
    const validation =
      validateName(name) ?? validateValue(value) ?? validateComment(comment);
    if (validation) {
      setCreateError(validation);
      return;
    }
    setCreateBusy(true);
    setCreateError(null);
    setNotice(null);
    clearCreateValue();
    try {
      const created = await createOrganizationSecret({
        organization_id: page.organizationId,
        name,
        value,
        ...(comment ? { comment } : {}),
      });
      setPage((current) =>
        current.status === "loaded"
          ? {
              ...current,
              secrets: [...current.secrets, created].sort((left, right) =>
                left.name.localeCompare(right.name),
              ),
            }
          : current,
      );
      setCreateOpen(false);
      setCreateForm(EMPTY_CREATE);
      openAccess(created);
      setNotice(`${created.name} was created. Add grants or bindings if needed.`);
    } catch (cause) {
      setCreateError(
        safeError(
          cause,
          "The secret could not be created. Check the name and try again.",
          "A secret with this name already exists.",
        ),
      );
    } finally {
      setCreateBusy(false);
      clearCreateValue();
    }
  }

  function openEdit(secret: OrganizationSecretSummary, mode: EditMode) {
    setEditTarget(secret);
    setEditMode(mode);
    setEditForm({ value: "", comment: secret.comment });
    setEditError(null);
    setNotice(null);
  }

  function resetEdit() {
    setEditTarget(null);
    setEditMode(null);
    setEditForm(EMPTY_EDIT);
    setEditError(null);
    if (editValueRef.current) editValueRef.current.value = "";
  }

  function closeEdit() {
    if (editBusy) return;
    resetEdit();
  }

  function clearEditValue() {
    setEditForm((current) => ({ ...current, value: "" }));
    if (editValueRef.current) editValueRef.current.value = "";
  }

  async function submitEdit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (page.status !== "loaded" || !editTarget || !editMode || editBusy) {
      return;
    }
    const value = editMode === "value" ? editForm.value : "";
    const comment = editForm.comment.trim();
    const validation =
      validateOptionalValue(value) ?? validateComment(comment);
    if (validation) {
      setEditError(validation);
      return;
    }
    const target = editTarget;
    setEditBusy(true);
    setEditError(null);
    setNotice(null);
    clearEditValue();
    try {
      const updated = await replaceOrganizationSecret(
        page.organizationId,
        target.name,
        {
          comment,
          ...(value ? { value } : {}),
        },
      );
      setPage((current) =>
        current.status === "loaded"
          ? {
              ...current,
              secrets: current.secrets.map((secret) =>
                secret.id === updated.id
                  ? {
                      ...updated,
                      grants: secret.grants,
                      bindings: secret.bindings,
                    }
                  : secret,
              ),
            }
          : current,
      );
      resetEdit();
      setNotice(
        value
          ? `${target.name} was replaced.`
          : `${target.name}'s comment was updated.`,
      );
    } catch (cause) {
      setEditError(
        safeError(
          cause,
          "The secret could not be updated. Please try again.",
        ),
      );
    } finally {
      setEditBusy(false);
      clearEditValue();
    }
  }

  function openDelete(secret: OrganizationSecretSummary) {
    setDeleteTarget(secret);
    setDeleteConfirmation("");
    setDeleteError(null);
    setNotice(null);
  }

  function closeDelete() {
    if (deleteBusy) return;
    setDeleteTarget(null);
    setDeleteConfirmation("");
    setDeleteError(null);
  }

  async function confirmDelete() {
    if (page.status !== "loaded" || !deleteTarget || deleteBusy) return;
    if (deleteConfirmation !== deleteTarget.name) return;
    const target = deleteTarget;
    setDeleteBusy(true);
    setDeleteError(null);
    setNotice(null);
    try {
      await deleteOrganizationSecret(page.organizationId, target.name);
      setPage((current) =>
        current.status === "loaded"
          ? {
              ...current,
              secrets: current.secrets.filter(
                (secret) => secret.id !== target.id,
              ),
            }
          : current,
      );
      setDeleteTarget(null);
      setDeleteConfirmation("");
      setNotice(`${target.name} was deleted.`);
    } catch (cause) {
      setDeleteError(
        safeError(cause, "The secret could not be deleted. Please try again."),
      );
    } finally {
      setDeleteBusy(false);
    }
  }

  function openAccess(secret: OrganizationSecretSummary) {
    setAccessTarget(secret);
    setAccessGrants(secret.grants);
    setAccessBindings(secret.bindings);
    setAccessPublisher("");
    setAccessKey("");
    setAccessPurpose("");
    setAccessError(null);
    setNotice(null);
  }

  function closeAccess() {
    if (accessBusy) return;
    setAccessTarget(null);
    setAccessGrants([]);
    setAccessBindings([]);
    setAccessPublisher("");
    setAccessKey("");
    setAccessPurpose("");
    setAccessError(null);
  }

  function addGrant() {
    const validation = validateExtension(accessPublisher, accessKey);
    if (validation) {
      setAccessError(validation);
      return;
    }
    const identity = { publisher: accessPublisher, key: accessKey };
    if (accessGrants.some((grant) => identityKey(grant) === identityKey(identity))) {
      setAccessError("That extension already has a grant.");
      return;
    }
    setAccessGrants((current) =>
      [...current, identity].sort((left, right) =>
        identityKey(left).localeCompare(identityKey(right)),
      ),
    );
    setAccessError(null);
  }

  function addBinding() {
    const validation =
      validateExtension(accessPublisher, accessKey) ??
      validatePurpose(accessPurpose);
    if (validation) {
      setAccessError(validation);
      return;
    }
    const identity = { publisher: accessPublisher, key: accessKey };
    if (
      accessBindings.some(
        (binding) =>
          identityKey(binding) === identityKey(identity) &&
          binding.purpose === accessPurpose,
      )
    ) {
      setAccessError("That purpose is already bound for this extension.");
      return;
    }
    setAccessBindings((current) =>
      [
        ...current,
        {
          publisher: identity.publisher,
          key: identity.key,
          purpose: accessPurpose,
          secret_name: accessTarget?.name ?? "",
        },
      ].sort((left, right) =>
        `${identityKey(left)}:${left.purpose}`.localeCompare(
          `${identityKey(right)}:${right.purpose}`,
        ),
      ),
    );
    setAccessPurpose("");
    setAccessError(null);
  }

  function removeGrant(identity: string) {
    setAccessGrants((current) =>
      current.filter((grant) => identityKey(grant) !== identity),
    );
  }

  function removeBinding(identity: string, purpose: string) {
    setAccessBindings((current) =>
      current.filter(
        (binding) =>
          !(
            identityKey(binding) === identity && binding.purpose === purpose
          ),
      ),
    );
  }

  async function saveAccess() {
    if (page.status !== "loaded" || !accessTarget || accessBusy) return;
    const target = accessTarget;
    setAccessBusy(true);
    setAccessError(null);
    setNotice(null);
    try {
      const beforeGrants = new Set(target.grants.map(identityKey));
      const afterGrants = new Set(accessGrants.map(identityKey));
      const grantsChanged =
        beforeGrants.size !== afterGrants.size ||
        [...afterGrants].some((identity) => !beforeGrants.has(identity));
      if (grantsChanged) {
        await replaceOrganizationSecretGrants(
          page.organizationId,
          target.name,
          accessGrants.map((grant) => ({
            publisher: grant.publisher,
            key: grant.key,
          })),
        );
      }
      const beforeBindings = groupedPurposes(target.bindings);
      const afterBindings = groupedPurposes(accessBindings);
      const identities = new Set([
        ...beforeBindings.keys(),
        ...afterBindings.keys(),
      ]);
      for (const identity of identities) {
        const before = beforeBindings.get(identity)?.purposes ?? [];
        const after = afterBindings.get(identity)?.purposes ?? [];
        const normalizedBefore = [...before].sort();
        const normalizedAfter = [...after].sort();
        if (
          normalizedBefore.length === normalizedAfter.length &&
          normalizedBefore.every(
            (purpose, index) => purpose === normalizedAfter[index],
          )
        ) {
          continue;
        }
        const [publisher, key] = identity.split("/");
        await replaceOrganizationSecretBindings(
          page.organizationId,
          target.name,
          { publisher, key, purposes: normalizedAfter },
        );
      }
      const secrets = await listOrganizationSecrets(page.organizationId);
      setPage((current) =>
        current.status === "loaded" ? { ...current, secrets } : current,
      );
      setAccessTarget(null);
      setNotice(`Access to ${target.name} was updated.`);
    } catch (cause) {
      setAccessError(
        safeError(
          cause,
          "The access change could not be saved. Please try again.",
          "The extension must be installed and its release must allow this secret.",
        ),
      );
    } finally {
      setAccessBusy(false);
    }
  }

  if (page.status === "loading") {
    return (
      <main className="mx-auto max-w-5xl px-6 py-10 text-sm text-[var(--muted-foreground)]">
        Loading organization secrets...
      </main>
    );
  }

  if (page.status === "error") {
    return (
      <main className="mx-auto max-w-5xl px-6 py-10">
        <p role="alert" className="text-sm text-[var(--danger)]">
          {page.message}
        </p>
      </main>
    );
  }

  const editTitle =
    editMode === "comment" ? "Edit secret comment" : "Replace secret value";

  return (
    <OrganizationNavigation
      organizationId={page.organizationId}
      organizations={page.organizations}
      onOrganizationChange={(nextOrganizationId) => {
        if (nextOrganizationId === page.organizationId) return;
        window.location.assign(
          organizationViewHref(nextOrganizationId, "secrets"),
        );
      }}
      view="secrets"
    >
      <div className="space-y-8">
        <PageHeader
          title="Secrets"
          description={
            page.organizationName
              ? `${page.organizationName} · Write-only values that granted extensions can use at runtime.`
              : "Write-only values that granted extensions can use at runtime."
          }
          actions={
            <Button onClick={openCreate} type="button">
              Create secret
            </Button>
          }
        />

        {notice ? (
          <p role="status" className="text-sm text-[var(--muted-foreground)]">
            {notice}
          </p>
        ) : null}

        <DataTableBlock
          actionsHeader="Actions"
          columns={SECRET_COLUMNS}
          emptyState="No organization secrets yet. Create one before granting it to an extension."
          getRowId={(secret) => secret.id}
          getRowLabel={(secret) => secret.name}
          onSelectedIdsChange={setSelectedSecretIds}
          rowActions={(secret) => [
            {
              id: `replace-${secret.id}`,
              label: "Replace value",
              ariaLabel: `Replace value for ${secret.name}`,
              onSelect: () => openEdit(secret, "value"),
            },
            {
              id: `comment-${secret.id}`,
              label: "Edit comment",
              ariaLabel: `Edit comment for ${secret.name}`,
              onSelect: () => openEdit(secret, "comment"),
            },
            {
              id: `access-${secret.id}`,
              label: "Manage access",
              ariaLabel: `Manage access for ${secret.name}`,
              onSelect: () => openAccess(secret),
            },
            {
              id: `delete-${secret.id}`,
              label: "Delete",
              ariaLabel: `Delete ${secret.name}`,
              onSelect: () => openDelete(secret),
            },
          ]}
          rowActionsAriaLabel={(secret) => `Actions for ${secret.name}`}
          rowActionsLabel="Actions"
          rows={page.secrets}
          selectable={false}
          selectedIds={selectedSecretIds}
          tableLabel="Organization secrets"
          testId="organization-secrets"
        />

        <Dialog
          open={createOpen}
          onOpenChange={(open) => {
            if (open) openCreate();
            else closeCreate();
          }}
          title="Create secret"
          description="The value is write-only. It is cleared from this form on submit and is never shown again or stored in this browser."
        >
          <form className="grid gap-4" onSubmit={(event) => void submitCreate(event)}>
            <label className="grid gap-1 text-sm font-medium">
              Name
              <Input
                autoCapitalize="none"
                autoComplete="off"
                id="organization-secret-name"
                maxLength={MAX_SECRET_NAME_CHARS}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                pattern="[a-z0-9_-]+"
                spellCheck={false}
                value={createForm.name}
              />
              <span className="text-xs font-normal text-[var(--muted-foreground)]">
                Lowercase letters, digits, underscores, and hyphens; up to 80
                characters. This name cannot be changed later.
              </span>
            </label>
            <label className="grid gap-1 text-sm font-medium">
              Value
              <Input
                autoComplete="new-password"
                id="organization-secret-value"
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    value: event.target.value,
                  }))
                }
                ref={createValueRef}
                spellCheck={false}
                type="password"
                value={createForm.value}
              />
              <span className="text-xs font-normal text-[var(--muted-foreground)]">
                1 to 8192 bytes. The value is never returned or displayed.
              </span>
            </label>
            <label className="grid gap-1 text-sm font-medium">
              Comment
              <Input
                autoComplete="off"
                id="organization-secret-comment"
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    comment: event.target.value,
                  }))
                }
                value={createForm.comment}
              />
            </label>
            {createError ? (
              <p role="alert" className="text-sm text-[var(--danger)]">
                {createError}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                disabled={createBusy}
                onClick={closeCreate}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button disabled={createBusy} type="submit">
                {createBusy ? "Creating..." : "Create secret"}
              </Button>
            </div>
          </form>
        </Dialog>

        <Dialog
          open={editTarget !== null}
          onOpenChange={(open) => {
            if (!open) closeEdit();
          }}
          title={editTitle}
          description={
            editTarget
              ? `Update ${editTarget.name}. A blank value changes only the comment.`
              : undefined
          }
        >
          <form className="grid gap-4" onSubmit={(event) => void submitEdit(event)}>
            {editMode === "value" ? (
              <label className="grid gap-1 text-sm font-medium">
                New value
                <Input
                  autoComplete="new-password"
                  id="organization-secret-new-value"
                  onChange={(event) =>
                    setEditForm((current) => ({
                      ...current,
                      value: event.target.value,
                    }))
                  }
                  ref={editValueRef}
                  spellCheck={false}
                  type="password"
                  value={editForm.value}
                />
                <span className="text-xs font-normal text-[var(--muted-foreground)]">
                  Leave blank to update only the comment. The value is cleared
                  from this form on submit.
                </span>
              </label>
            ) : null}
            <label className="grid gap-1 text-sm font-medium">
              Comment
              <Input
                autoComplete="off"
                id="organization-secret-edit-comment"
                onChange={(event) =>
                  setEditForm((current) => ({
                    ...current,
                    comment: event.target.value,
                  }))
                }
                value={editForm.comment}
              />
            </label>
            {editError ? (
              <p role="alert" className="text-sm text-[var(--danger)]">
                {editError}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                disabled={editBusy}
                onClick={closeEdit}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button disabled={editBusy} type="submit">
                {editBusy
                  ? "Saving..."
                  : editMode === "comment"
                    ? "Save comment"
                    : "Replace value"}
              </Button>
            </div>
          </form>
        </Dialog>

        <Dialog
          open={deleteTarget !== null}
          onOpenChange={(open) => {
            if (!open) closeDelete();
          }}
          title="Delete secret"
          description="This permanently removes the secret and its grants and bindings. Extensions using it lose access. It cannot be undone."
        >
          <div className="grid gap-4">
            <p className="text-sm text-[var(--muted-foreground)]">
              {deleteTarget
                ? `Delete ${deleteTarget.name}? Type its name exactly to confirm.`
                : null}
            </p>
            <label className="grid gap-1 text-sm font-medium">
              {deleteTarget
                ? `Type ${deleteTarget.name} to confirm`
                : "Type the secret name to confirm"}
              <Input
                autoComplete="off"
                id="organization-secret-delete-confirmation"
                onChange={(event) =>
                  setDeleteConfirmation(event.target.value)
                }
                spellCheck={false}
                value={deleteConfirmation}
              />
            </label>
            {deleteError ? (
              <p role="alert" className="text-sm text-[var(--danger)]">
                {deleteError}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                disabled={deleteBusy}
                onClick={closeDelete}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                disabled={
                  deleteBusy ||
                  !deleteTarget ||
                  deleteConfirmation !== deleteTarget.name
                }
                onClick={() => void confirmDelete()}
                type="button"
                variant="destructive"
              >
                {deleteBusy ? "Deleting..." : "Delete permanently"}
              </Button>
            </div>
          </div>
        </Dialog>

        <Dialog
          open={accessTarget !== null}
          onOpenChange={(open) => {
            if (!open) closeAccess();
          }}
          title="Manage secret access"
          description={
            accessTarget
              ? `Grant extensions and set binding purposes for ${accessTarget.name}. The extension must be installed and its release must allowlist this secret.`
              : undefined
          }
        >
          <div className="grid gap-6">
            <section className="grid gap-3">
              <h3 className="text-sm font-semibold">Granted extensions</h3>
              {accessGrants.length === 0 ? (
                <p className="text-sm text-[var(--muted-foreground)]">
                  No extensions are granted this secret.
                </p>
              ) : (
                <ul className="grid gap-2">
                  {accessGrants.map((grant) => {
                    const identity = identityKey(grant);
                    return (
                      <li
                        key={identity}
                        className="flex items-center justify-between gap-3 rounded-lg border border-[var(--border)] px-3 py-2 text-sm"
                      >
                        <span>{identity}</span>
                        <Button
                          aria-label={`Remove grant for ${identity}`}
                          onClick={() => removeGrant(identity)}
                          size="sm"
                          type="button"
                          variant="ghost"
                        >
                          Remove
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="grid gap-1 text-sm font-medium">
                  Publisher
                  <Input
                    autoCapitalize="none"
                    autoComplete="off"
                    id="organization-secret-access-publisher"
                    onChange={(event) => setAccessPublisher(event.target.value)}
                    spellCheck={false}
                    value={accessPublisher}
                  />
                </label>
                <label className="grid gap-1 text-sm font-medium">
                  Extension key
                  <Input
                    autoCapitalize="none"
                    autoComplete="off"
                    id="organization-secret-access-key"
                    onChange={(event) => setAccessKey(event.target.value)}
                    spellCheck={false}
                    value={accessKey}
                  />
                </label>
              </div>
              <div>
                <Button onClick={addGrant} size="sm" type="button" variant="outline">
                  Add grant
                </Button>
              </div>
            </section>

            <section className="grid gap-3">
              <h3 className="text-sm font-semibold">Binding purposes</h3>
              {accessBindings.length === 0 ? (
                <p className="text-sm text-[var(--muted-foreground)]">
                  No binding purposes point at this secret.
                </p>
              ) : (
                <ul className="grid gap-2">
                  {accessBindings.map((binding) => {
                    const identity = identityKey(binding);
                    return (
                      <li
                        key={`${identity}:${binding.purpose}`}
                        className="flex items-center justify-between gap-3 rounded-lg border border-[var(--border)] px-3 py-2 text-sm"
                      >
                        <span>
                          {identity} · {binding.purpose}
                        </span>
                        <Button
                          aria-label={`Remove binding ${binding.purpose} for ${identity}`}
                          onClick={() => removeBinding(identity, binding.purpose)}
                          size="sm"
                          type="button"
                          variant="ghost"
                        >
                          Remove
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}
              <label className="grid gap-1 text-sm font-medium">
                Purpose
                <Input
                  autoCapitalize="none"
                  autoComplete="off"
                  id="organization-secret-access-purpose"
                  onChange={(event) => setAccessPurpose(event.target.value)}
                  placeholder="user_key:license_public_id"
                  spellCheck={false}
                  value={accessPurpose}
                />
                <span className="text-xs font-normal text-[var(--muted-foreground)]">
                  Uses the publisher and extension key above. Purposes are opaque
                  to the platform and must be unique per extension.
                </span>
              </label>
              <div>
                <Button onClick={addBinding} size="sm" type="button" variant="outline">
                  Add binding
                </Button>
              </div>
            </section>

            {accessError ? (
              <p role="alert" className="text-sm text-[var(--danger)]">
                {accessError}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                disabled={accessBusy}
                onClick={closeAccess}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                disabled={accessBusy}
                onClick={() => void saveAccess()}
                type="button"
              >
                {accessBusy ? "Saving..." : "Save access"}
              </Button>
            </div>
          </div>
        </Dialog>
      </div>
    </OrganizationNavigation>
  );
}
